// Proposal `origin` frontmatter is a machine identifier — e.g.
// `promotion:<tenantId>:<entryName>` (ADR-0148 §5.3) or `session:<id>` from
// capture paths — not display text. Turn it into a human header; a raw UUID
// must never reach the UI, so unknown shapes are scrubbed before rendering.
// Kept out of the route component so it is unit-testable (console tests are
// pure-function tests; there is no render harness).

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export function proposalOriginLabel(
  origin: string | null | undefined,
  tenantNameById: Map<string, string>
): string {
  if (!origin) return "No origin";

  const promotion = origin.match(/^promotion:([0-9a-f-]{36}):(.+)$/i);
  if (promotion) {
    const tenantName = tenantNameById.get(promotion[1].toLowerCase());
    return tenantName ? `Promotion from ${tenantName} · ${promotion[2]}` : `Promotion · ${promotion[2]}`;
  }

  const session = origin.match(/^session:[0-9a-f-]{36}(.*)$/i);
  if (session) {
    const detail = session[1].replace(/[()_]/g, " ").replace(/\s+/g, " ").trim();
    return detail ? `Session capture · ${detail}` : "Session capture";
  }

  const scrubbed = origin
    .replace(UUID_RE, "")
    .replace(/:{2,}/g, ":")
    .replace(/^[\s:·]+|[\s:·]+$/g, "");
  return scrubbed || "Capture";
}

/**
 * Who filed a proposal, as a sentence fragment a curator can read.
 *
 * `proposed_by` and `updated_by` are machine identifiers — `account:Ryan Cooper`,
 * `key:bd-nightly`, `capture:mcp`, `extractor`. None of them may reach the
 * screen as written.
 *
 * The order matters, and it is the whole point of this column: a curator is
 * reading it to answer "who do I take this up with". So the PERSON wins over
 * the mechanism, every time.
 *
 *  1. `proposed_by` names the filer outright — a person, or the key they used.
 *  2. Otherwise, if this came out of an Atlas Query session, the person whose
 *     session it was. The extractor is a machine that reads finished sessions
 *     and drafts proposals from them; saying "the extractor" would name the
 *     sweep and hide the human who actually said the thing. Nobody reviews a
 *     correction with a sweep.
 *  3. Only when neither exists does this say so. Every proposal filed before
 *     the filer stamp shipped is in that state, and a plausible-sounding guess
 *     would be worse than the blank — a curator would act on it.
 */
export function proposedByLabel(
  proposedBy: string | null | undefined,
  updatedBy: string | null | undefined,
  /** The person whose Atlas Query session this was extracted from, when it was. */
  sessionOwner?: string | null
): string {
  const account = /^account:(.+)$/.exec(proposedBy ?? "");
  if (account && account[1].trim()) return account[1].trim();

  const key = /^key:(.+)$/.exec(proposedBy ?? "");
  if (key && key[1].trim()) return `the ${key[1].trim()} key`;

  if (sessionOwner && sessionOwner.trim()) return sessionOwner.trim();

  // The machine is named only when its session's owner cannot be resolved —
  // the session was deleted, or the account was. Better than "Not recorded",
  // because something specific did produce this.
  if ((updatedBy ?? "") === "extractor") return "Extracted from a session";

  return "Not recorded";
}
